import { useEffect, useState } from 'react';
import {
  Button,
  DialogCloseTrigger,
  DialogFooter,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';
import { CaretDown, Plus, X } from '@phosphor-icons/react';
import { DialogActionTrigger, DialogBody, DialogContent, DialogRoot } from '@/components/ui/dialog';
import { fetchAPI } from '@/api/fetch';
import { Job, JobBasicInfo } from '@/types/Job';
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Field } from '@/components/ui/field';
import { apiGetJobCategories, GetJobCategoriesResponse } from '@/api/services';

interface PublishJobDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (category?: string) => void;
  job: Job | JobBasicInfo | null;
  isUpdating?: boolean;
  isPublishing: boolean;
  isLoading: boolean;
  error: string | null;
}

export default function PublishJobDialog({
  isOpen,
  onClose,
  onConfirm,
  job,
  isUpdating = false,
  isPublishing,
  isLoading,
  error
}: PublishJobDialogProps) {
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newCategory, setNewCategory] = useState<string>('');
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && isPublishing) {
      loadCategories();
      setSelectedCategory(job?.category || '');
      setCategoryError(null);
    }
  }, [isOpen, isPublishing, job]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await fetchAPI<GetJobCategoriesResponse>({
        request: () => apiGetJobCategories()
      });

      if (response.success && response.data) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      setSelectedCategory(newCategory.trim());
      setNewCategory('');
      setCategoryError(null);
    }
  };

  const handleConfirm = () => {
    if (isPublishing && !selectedCategory.trim()) {
      setCategoryError('Please select a category');
      return;
    }

    onConfirm(isPublishing ? selectedCategory : undefined);
  };

  return (
    <DialogRoot
      size="lg"
      open={isOpen}
      onOpenChange={(e) => !e.open && !isLoading && onClose()}
      closeOnInteractOutside={!isLoading}
    >
      <DialogContent borderRadius="12px" p="20px 12px 12px 12px">
        <DialogBody>
          <Stack gap="16px" w="100%">
            <Text fontSize="20px" fontWeight="bold" color="neutrals.900">
              {isUpdating ? 'Update Category' : isPublishing ? 'Publish Job' : 'Unpublish Job'}
            </Text>

            <Text fontSize="16px" color="neutrals.700">
              {isUpdating
                ? 'Please select a new category for this job. This will update its visibility on the job board.'
                : isPublishing
                ? 'Please select a category for this job before publishing it to the job board so it’s visible to potential candidates.'
                : 'Are you sure you want to unpublish this job from the job board? It will no longer be visible to potential candidates.'}
            </Text>

            {isPublishing && (
              <Field
                label={<RequiredFieldLabel>Category</RequiredFieldLabel>}
                errorText={categoryError}
              >
                {categoriesLoading ? (
                  <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text>Loading categories...</Text>
                  </Flex>
                ) : (
                  <Stack w="100%" align="stretch" gap={2}>
                    <Menu.Root positioning={{ placement: 'bottom-start' }}>
                      <Menu.Trigger asChild disabled={isLoading}>
                        <Button
                          variant="outline"
                          border="1px solid"
                          borderRadius="8px"
                          borderColor={categoryError ? 'error.500' : 'neutrals.200'}
                          w="100%"
                          textAlign="left"
                          fontSize="16px"
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            w="100%"
                            color={selectedCategory ? 'neutrals.900' : 'neutrals.500'}
                          >
                            {selectedCategory || 'Select a category'}
                            <CaretDown size={16} />
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content borderRadius="8px">
                          {categories.length > 0 && (
                            <>
                              {categories.map((category) => (
                                <Menu.Item
                                  key={category}
                                  value={category}
                                  cursor="pointer"
                                  fontSize="16px"
                                  onClick={() => {
                                    setSelectedCategory(category);
                                    setCategoryError(null);
                                  }}
                                >
                                  {category}
                                </Menu.Item>
                              ))}
                              <Menu.Separator />
                            </>
                          )}
                          <Menu.Item
                            value="new-category-input"
                            closeOnSelect={false}
                            _hover={{ bg: 'transparent' }}
                          >
                            <HStack w="100%" onClick={(e) => e.stopPropagation()}>
                              <Input
                                fontSize="16px"
                                placeholder="Enter new category"
                                value={newCategory}
                                size="sm"
                                onChange={(e) => setNewCategory(e.target.value)}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    handleAddNewCategory();
                                  }
                                }}
                              />
                              <IconButton
                                variant="ghost"
                                size="sm"
                                color="primary.900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddNewCategory();
                                }}
                                disabled={!newCategory.trim()}
                              >
                                <Plus size={16} weight="bold" />
                              </IconButton>
                            </HStack>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  </Stack>
                )}
              </Field>
            )}

            {error && (
              <Text color="error.500" fontSize="14px">
                {error}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter gap="12px">
          <DialogActionTrigger asChild>
            <Button
              variant="outline"
              border="1px solid"
              borderColor="primary.900"
              color="primary.900"
              bg="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              _hover={{ bg: 'neutrals.100' }}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            fontSize="16px"
            _hover={{ bg: 'primary.700' }}
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {isUpdating ? 'Update' : isPublishing ? 'Publish' : 'Unpublish'}
          </Button>
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={isLoading} cursor="pointer">
          <X size={20} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
